import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { ArrowLeft, RefreshCw, Gift, FileText, Users, Loader2, CheckCircle2, XCircle, Clock, AlertCircle, BadgeCheck, Package, Plus, Pencil, Trash2, ShoppingCart, Truck, Search, LogOut } from "lucide-react";
import axios from "axios";
import AdminLogin from "./AdminLogin";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/v1`;
const API_KEY = "taj-cement-secret-key-2024";

// Small presentational helper for label/value pairs in the order details modal
const Detail = ({ label, value, mono }) => (
  <div>
    <p className="text-xs text-muted-foreground uppercase tracking-wide">{label}</p>
    <p className={`text-sm ${mono ? "font-mono" : ""} break-words`}>{value || "-"}</p>
  </div>
);

// Check if admin session is valid
const checkAdminSession = () => {
  try {
    const session = JSON.parse(localStorage.getItem("adminSession") || "{}");
    if (session.authenticated && session.expiresAt) {
      if (new Date(session.expiresAt) > new Date()) {
        return true;
      }
    }
  } catch (e) {
    console.error("Session check error:", e);
  }
  return false;
};

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(checkAdminSession());
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [stats, setStats] = useState(null);
  const [sessions, setSessions] = useState({ rewards_sessions: [] });
  const [callbacks, setCallbacks] = useState([]);
  const [verifiedUsers, setVerifiedUsers] = useState([]);
  
  // Catalog state
  const [catalogProducts, setCatalogProducts] = useState([]);
  const [catalogTotal, setCatalogTotal] = useState(0);
  const [catalogPage, setCatalogPage] = useState(1);
  const [catalogSearch, setCatalogSearch] = useState("");
  const [catalogCategory, setCatalogCategory] = useState("all");
  const [categories, setCategories] = useState([]);
  
  // Orders state
  const [orders, setOrders] = useState([]);
  const [orderStats, setOrderStats] = useState({});
  const [orderFilter, setOrderFilter] = useState("all");
  const [userTypeFilter, setUserTypeFilter] = useState("all");
  const [userTypes, setUserTypes] = useState([]);
  const [orderStartDate, setOrderStartDate] = useState("");
  const [orderEndDate, setOrderEndDate] = useState("");
  const [orderIdSearch, setOrderIdSearch] = useState("");
  const [mobileSearch, setMobileSearch] = useState("");
  const [orderPage, setOrderPage] = useState(1);
  const [orderPages, setOrderPages] = useState(1);
  const [orderLimit] = useState(50);
  const [exportLoading, setExportLoading] = useState(false);
  const [backfillLoading, setBackfillLoading] = useState(false);
  const [clearOldDataLoading, setClearOldDataLoading] = useState(false);
  const [tajReportLoading, setTajReportLoading] = useState(false);
  
  // Active tab state for clickable stats
  const [activeTab, setActiveTab] = useState("orders");
  
  // Verified users filter state
  const [verifiedUserTypeFilter, setVerifiedUserTypeFilter] = useState("all");
  
  // Product form
  const [productDialogOpen, setProductDialogOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [productForm, setProductForm] = useState({
    name: "", brand: "", description: "", category: "", item_type: "physical",
    points_required: "", mrp: "", transfer_price: "", image_url: "",
    hsn_code: "", tax_percent: "18", provider: "TagNPay Rewards", in_stock: true
  });
  const [productLoading, setProductLoading] = useState(false);
  
  // CSV Upload
  const [uploadLoading, setUploadLoading] = useState(false);
  const fileInputRef = useState(null);
  
  // Order status dialog
  const [orderDialogOpen, setOrderDialogOpen] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [orderStatusForm, setOrderStatusForm] = useState({
    status: "", tracking_number: "", courier_partner: "", notes: ""
  });

  // Order details drill-down modal
  const [orderDetailsOpen, setOrderDetailsOpen] = useState(false);
  const [detailsOrder, setDetailsOrder] = useState(null);

  // Caller info form (inside Order Details dialog)
  const [callerForm, setCallerForm] = useState({
    caller_name: "",
    taj_confirmation: "pending",
    delivery_address: { line1: "", line2: "", city: "", district: "", state: "", pincode: "" },
  });
  const [callerSaving, setCallerSaving] = useState(false);
  const [deleteOrderLoading, setDeleteOrderLoading] = useState(false);

  // API Logs state
  const [apiLogs, setApiLogs] = useState([]);
  const [apiLogsStats, setApiLogsStats] = useState({});
  const [apiLogsPage, setApiLogsPage] = useState(1);
  const [apiLogsPages, setApiLogsPages] = useState(1);
  const [apiLogsFilteredTotal, setApiLogsFilteredTotal] = useState(0);
  const [apiLogsFilters, setApiLogsFilters] = useState({
    method: "all",
    status_class: "all",
    path_contains: "",
    start_date: "",
    end_date: "",
  });
  const [apiLogsLoading, setApiLogsLoading] = useState(false);
  const [logDetailsOpen, setLogDetailsOpen] = useState(false);
  const [selectedLog, setSelectedLog] = useState(null);
  const [clearLogsLoading, setClearLogsLoading] = useState(false);

  useEffect(() => {
    if (isAuthenticated) {
      loadAllData();
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (isAuthenticated) {
      loadCatalog();
    }
  }, [catalogPage, catalogSearch, catalogCategory, isAuthenticated]);
  
  useEffect(() => {
    if (isAuthenticated) {
      loadOrders();
    }
  }, [orderFilter, userTypeFilter, orderStartDate, orderEndDate, orderPage, orderIdSearch, mobileSearch, isAuthenticated]);

  // Reset to page 1 whenever filters change
  useEffect(() => {
    setOrderPage(1);
  }, [orderFilter, userTypeFilter, orderStartDate, orderEndDate, orderIdSearch, mobileSearch]);

  // Load API logs whenever page or filters change (only when tab is active)
  useEffect(() => {
    if (isAuthenticated && activeTab === "api-logs") {
      loadApiLogs();
    }
  }, [apiLogsPage, apiLogsFilters, activeTab, isAuthenticated]);

  // Reset API logs page to 1 when filters change
  useEffect(() => {
    setApiLogsPage(1);
  }, [apiLogsFilters]);

  const loadApiLogs = async () => {
    setApiLogsLoading(true);
    try {
      const params = new URLSearchParams();
      if (apiLogsFilters.method !== "all") params.append("method", apiLogsFilters.method);
      if (apiLogsFilters.status_class !== "all") params.append("status_class", apiLogsFilters.status_class);
      if (apiLogsFilters.path_contains) params.append("path_contains", apiLogsFilters.path_contains);
      if (apiLogsFilters.start_date) params.append("start_date", apiLogsFilters.start_date);
      if (apiLogsFilters.end_date) params.append("end_date", apiLogsFilters.end_date);
      params.append("page", apiLogsPage);
      params.append("limit", 50);
      const res = await axios.get(`${API}/admin/api-logs?${params}`, { headers: { "X-API-Key": API_KEY } });
      setApiLogs(res.data.logs || []);
      setApiLogsStats(res.data.stats || {});
      setApiLogsPages(res.data.pages || 1);
      setApiLogsFilteredTotal(res.data.filtered_total || 0);
    } catch (error) {
      toast.error("Failed to load API logs");
    } finally {
      setApiLogsLoading(false);
    }
  };

  const handleClearApiLogs = async (olderThanDays) => {
    const msg = olderThanDays != null
      ? `Delete API logs older than ${olderThanDays} days?`
      : "Delete ALL API logs? This cannot be undone.";
    if (!confirm(msg)) return;
    setClearLogsLoading(true);
    try {
      const url = olderThanDays != null
        ? `${API}/admin/api-logs?older_than_days=${olderThanDays}`
        : `${API}/admin/api-logs`;
      const res = await axios.delete(url, { headers: { "X-API-Key": API_KEY } });
      toast.success(res.data.message);
      await loadApiLogs();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to clear API logs");
    } finally {
      setClearLogsLoading(false);
    }
  };

  const openLogDetails = (log) => {
    setSelectedLog(log);
    setLogDetailsOpen(true);
  };

  const handleLogout = () => {
    localStorage.removeItem("adminSession");
    setIsAuthenticated(false);
    toast.success("Logged out successfully");
  };

  // If not authenticated, show login page
  if (!isAuthenticated) {
    return <AdminLogin onLogin={setIsAuthenticated} />;
  }

  const loadAllData = async () => {
    try {
      const [statsRes, sessionsRes, callbacksRes, verifiedRes, categoriesRes, ordersRes] = await Promise.all([
        axios.get(`${API}/admin/dashboard-stats`, { headers: { "X-API-Key": API_KEY } }),
        axios.get(`${API}/admin/sessions`, { headers: { "X-API-Key": API_KEY } }),
        axios.get(`${API}/admin/callbacks`, { headers: { "X-API-Key": API_KEY } }),
        axios.get(`${API}/admin/verified-users`, { headers: { "X-API-Key": API_KEY } }),
        axios.get(`${API}/admin/categories`, { headers: { "X-API-Key": API_KEY } }),
        axios.get(`${API}/admin/orders`, { headers: { "X-API-Key": API_KEY } })
      ]);

      setStats(statsRes.data);
      setSessions(sessionsRes.data);
      setCallbacks(callbacksRes.data);
      setVerifiedUsers(verifiedRes.data.users || []);
      setCategories(categoriesRes.data.categories || []);
      setOrders(ordersRes.data.orders || []);
      setOrderStats(ordersRes.data.stats || {});
    } catch (error) {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  const loadCatalog = async () => {
    try {
      const params = new URLSearchParams({ page: catalogPage, limit: 20 });
      if (catalogSearch) params.append("search", catalogSearch);
      if (catalogCategory && catalogCategory !== "all") params.append("category", catalogCategory);
      
      const res = await axios.get(`${API}/admin/catalog?${params}`, { headers: { "X-API-Key": API_KEY } });
      setCatalogProducts(res.data.products || []);
      setCatalogTotal(res.data.total || 0);
    } catch (error) {
      console.error("Failed to load catalog");
    }
  };

  const loadOrders = async () => {
    try {
      const params = new URLSearchParams();
      if (orderFilter && orderFilter !== "all") params.append("status", orderFilter);
      if (userTypeFilter && userTypeFilter !== "all") params.append("user_type", userTypeFilter);
      if (orderStartDate) params.append("start_date", orderStartDate);
      if (orderEndDate) params.append("end_date", orderEndDate);
      if (orderIdSearch.trim()) params.append("order_id", orderIdSearch.trim());
      if (mobileSearch.trim()) params.append("mobile", mobileSearch.trim());
      params.append("page", orderPage);
      params.append("limit", orderLimit);

      const res = await axios.get(`${API}/admin/orders?${params}`, { headers: { "X-API-Key": API_KEY } });
      setOrders(res.data.orders || []);
      setOrderStats(res.data.stats || {});
      setUserTypes(res.data.user_types || []);
      setOrderPages(res.data.pages || 1);
    } catch (error) {
      console.error("Failed to load orders");
    }
  };

  const handleExportOrders = async (format = 'csv') => {
    setExportLoading(true);
    try {
      const params = new URLSearchParams({ format });
      if (orderFilter && orderFilter !== "all") params.append("status", orderFilter);
      if (userTypeFilter && userTypeFilter !== "all") params.append("user_type", userTypeFilter);
      if (orderStartDate) params.append("start_date", orderStartDate);
      if (orderEndDate) params.append("end_date", orderEndDate);
      
      const res = await axios.get(`${API}/admin/orders/export?${params}`, { headers: { "X-API-Key": API_KEY } });
      
      if (format === 'csv' && res.data.csv_data) {
        // Download CSV
        const blob = new Blob([res.data.csv_data], { type: 'text/csv;charset=utf-8;' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = res.data.filename || 'orders_export.csv';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        toast.success(`Exported ${res.data.total} orders to CSV`);
      } else if (format === 'json') {
        // Download JSON
        const blob = new Blob([JSON.stringify(res.data.orders, null, 2)], { type: 'application/json' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `orders_export_${new Date().toISOString().slice(0,10)}.json`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        toast.success(`Exported ${res.data.total} orders to JSON`);
      }
    } catch (error) {
      toast.error("Failed to export orders");
    } finally {
      setExportLoading(false);
    }
  };

  const handleBackfillUserTypes = async () => {
    setBackfillLoading(true);
    try {
      const res = await axios.post(`${API}/admin/orders/backfill-user-type`, {}, { headers: { "X-API-Key": API_KEY } });
      toast.success(`Backfill complete: ${res.data.updated} orders updated`);
      // Reload orders to show updated data
      loadOrders();
    } catch (error) {
      toast.error("Failed to backfill user types");
    } finally {
      setBackfillLoading(false);
    }
  };

  const handleDownloadTajReport = async () => {
    setTajReportLoading(true);
    try {
      const params = new URLSearchParams({ fmt: "csv" });
      if (orderStartDate) params.append("start_date", orderStartDate);
      if (orderEndDate) params.append("end_date", orderEndDate);
      const res = await axios.get(`${API}/taj/influencer-gift-report?${params}`, { headers: { "X-API-Key": API_KEY } });
      const blob = new Blob([res.data.csv_data], { type: "text/csv" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = res.data.filename || "taj_influencer_gift_report.csv";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success(`Downloaded ${res.data.total} rows`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to download Taj report");
    } finally {
      setTajReportLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await loadAllData();
    await loadCatalog();
    setRefreshing(false);
    toast.success("Data refreshed");
  };

  const handleClearOldData = async () => {
    if (!confirm("This will permanently delete ALL orders, sessions, logs, and verified users created before 21-Apr-2026. Continue?")) return;
    setClearOldDataLoading(true);
    try {
      const res = await axios.post(`${API}/admin/clear-old-data`, {}, { headers: { "X-API-Key": API_KEY } });
      toast.success(res.data.message);
      await loadAllData();
      await loadCatalog();
      await loadOrders();
    } catch (error) {
      toast.error("Failed to clear old data");
    } finally {
      setClearOldDataLoading(false);
    }
  };

  const handleCSVUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    if (!file.name.endsWith('.csv')) {
      toast.error("Please select a CSV file");
      return;
    }
    
    setUploadLoading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const res = await axios.post(`${API}/admin/catalog/bulk-upload`, formData, {
        headers: {
          "X-API-Key": API_KEY,
          "Content-Type": "multipart/form-data"
        }
      });
      
      const { products_added, products_updated, total_errors } = res.data;
      toast.success(`Upload complete! Added: ${products_added}, Updated: ${products_updated}${total_errors > 0 ? `, Errors: ${total_errors}` : ''}`);
      
      // Refresh data
      await loadCatalog();
      await loadAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to upload CSV");
    } finally {
      setUploadLoading(false);
      // Reset file input
      event.target.value = '';
    }
  };

  const handleClearCatalog = async () => {
    if (!confirm("Are you sure you want to delete ALL products? This cannot be undone!")) return;
    
    try {
      await axios.delete(`${API}/admin/catalog/clear-all`, { headers: { "X-API-Key": API_KEY } });
      toast.success("All products deleted");
      await loadCatalog();
      await loadAllData();
    } catch (error) {
      toast.error("Failed to clear catalog");
    }
  };

  const handleSaveProduct = async () => {
    if (!productForm.name || !productForm.points_required) {
      toast.error("Name and Points Required are mandatory");
      return;
    }

    setProductLoading(true);
    try {
      const payload = {
        ...productForm,
        points_required: parseInt(productForm.points_required),
        mrp: productForm.mrp ? parseInt(productForm.mrp) : null,
        transfer_price: productForm.transfer_price ? parseInt(productForm.transfer_price) : null
      };
      
      if (editingProduct) {
        await axios.put(`${API}/admin/catalog/${editingProduct.id}`, payload, { headers: { "X-API-Key": API_KEY } });
        toast.success("Product updated");
      } else {
        await axios.post(`${API}/admin/catalog`, payload, { headers: { "X-API-Key": API_KEY } });
        toast.success("Product created");
      }
      
      setProductDialogOpen(false);
      resetProductForm();
      loadCatalog();
      loadAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save product");
    } finally {
      setProductLoading(false);
    }
  };

  const handleDeleteProduct = async (productId) => {
    if (!confirm("Are you sure you want to delete this product?")) return;
    
    try {
      await axios.delete(`${API}/admin/catalog/${productId}`, { headers: { "X-API-Key": API_KEY } });
      toast.success("Product deleted");
      loadCatalog();
      loadAllData();
    } catch (error) {
      toast.error("Failed to delete product");
    }
  };

  const openEditProduct = (product) => {
    setEditingProduct(product);
    setProductForm({
      name: product.name || "",
      brand: product.brand || "",
      description: product.description || "",
      category: product.category || "",
      item_type: product.item_type || "physical",
      points_required: product.points_required?.toString() || "",
      mrp: product.mrp?.toString() || "",
      transfer_price: product.transfer_price?.toString() || "",
      image_url: product.image_url || "",
      hsn_code: product.hsn_code || "",
      tax_percent: product.tax_percent || "18",
      provider: product.provider || "TagNPay Rewards",
      in_stock: product.in_stock !== false
    });
    setProductDialogOpen(true);
  };

  const resetProductForm = () => {
    setEditingProduct(null);
    setProductForm({
      name: "", brand: "", description: "", category: "", item_type: "physical",
      points_required: "", mrp: "", transfer_price: "", image_url: "",
      hsn_code: "", tax_percent: "18", provider: "TagNPay Rewards", in_stock: true
    });
  };

  const openOrderStatusDialog = (order) => {
    setSelectedOrder(order);
    setOrderStatusForm({
      status: order.fulfillment_status || "pending",
      tracking_number: order.tracking_number || "",
      courier_partner: order.courier_partner || "",
      notes: ""
    });
    setOrderDialogOpen(true);
  };

  const openOrderDetails = (order) => {
    setDetailsOrder(order);
    const addr = order.delivery_address || {};
    setCallerForm({
      caller_name: order.caller_name || "",
      taj_confirmation: order.taj_confirmation || "pending",
      delivery_address: {
        line1: addr.line1 || "",
        line2: addr.line2 || "",
        city: addr.city || "",
        district: addr.district || "",
        state: addr.state || "",
        pincode: addr.pincode || "",
      },
    });
    setOrderDetailsOpen(true);
  };

  const handleDeleteOrder = async () => {
    if (!detailsOrder) return;
    if (!confirm(`Permanently delete order ${detailsOrder.order_id}? This cannot be undone.`)) return;
    setDeleteOrderLoading(true);
    try {
      await axios.delete(`${API}/admin/orders/${detailsOrder.order_id}`, { headers: { "X-API-Key": API_KEY } });
      toast.success(`Order ${detailsOrder.order_id} deleted`);
      setOrderDetailsOpen(false);
      setDetailsOrder(null);
      loadOrders();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete order");
    } finally {
      setDeleteOrderLoading(false);
    }
  };

  const handleSaveCallerInfo = async () => {
    if (!detailsOrder) return;
    setCallerSaving(true);
    try {
      const res = await axios.put(
        `${API}/admin/orders/${detailsOrder.order_id}/caller-info`,
        callerForm,
        { headers: { "X-API-Key": API_KEY } }
      );
      toast.success("Caller info saved");
      setDetailsOrder(res.data.order || { ...detailsOrder, ...callerForm });
      loadOrders();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save caller info");
    } finally {
      setCallerSaving(false);
    }
  };

  const handleUpdateOrderStatus = async () => {
    if (!selectedOrder) return;
    
    try {
      await axios.put(`${API}/admin/orders/${selectedOrder.order_id}/status`, orderStatusForm, { headers: { "X-API-Key": API_KEY } });
      toast.success("Order status updated");
      setOrderDialogOpen(false);
      loadAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update order status");
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: "bg-yellow-100 text-yellow-800",
      processing: "bg-blue-100 text-blue-800",
      shipped: "bg-purple-100 text-purple-800",
      delivered: "bg-green-100 text-green-800",
      cancelled: "bg-red-100 text-red-800"
    };
    return <Badge className={styles[status] || "bg-gray-100 text-gray-800"}>{status || "pending"}</Badge>;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-12 h-12 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="bg-primary text-primary-foreground py-4 px-4 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" className="text-primary-foreground hover:bg-primary-foreground/10" onClick={() => navigate("/")}>
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <img src="https://customer-assets-agu9un31.emergentagent.net/job_a79b9f2f-e1ce-41bf-8c44-d5f2616bf672/artifacts/mx91th95_TAJ.png" alt="Taj Cement — The Honest Cement" className="h-10 w-auto rounded-sm" />
            <h1 className="text-xl font-bold tracking-tight" style={{ fontFamily: 'Manrope' }}>ADMIN DASHBOARD</h1>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" className="bg-transparent border-primary-foreground/30 text-primary-foreground" onClick={handleClearOldData} disabled={clearOldDataLoading} data-testid="clear-old-data-btn">
              {clearOldDataLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Trash2 className="w-4 h-4 mr-2" />}
              Clear Old Data
            </Button>
            <Button variant="outline" size="sm" className="bg-transparent border-primary-foreground/30 text-primary-foreground" onClick={handleRefresh} disabled={refreshing}>
              <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? "animate-spin" : ""}`} />
              Refresh
            </Button>
            <Button variant="outline" size="sm" className="bg-transparent border-primary-foreground/30 text-primary-foreground" onClick={handleLogout}>
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </header>

      {/* Stats Cards */}
      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <Card className="rounded-sm cursor-pointer hover:ring-2 hover:ring-primary/50 transition-all" data-testid="stat-total-orders" onClick={() => setActiveTab("orders")}>
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-muted-foreground uppercase">Total Orders</p>
                  <p className="text-2xl font-bold">{stats?.orders?.total || 0}</p>
                </div>
                <ShoppingCart className="w-8 h-8 text-primary/30" />
              </div>
            </CardContent>
          </Card>
          <Card className="rounded-sm cursor-pointer hover:ring-2 hover:ring-yellow-500/50 transition-all" data-testid="stat-pending-orders" onClick={() => { setActiveTab("orders"); setOrderFilter("pending"); }}>
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-muted-foreground uppercase">Pending Orders</p>
                  <p className="text-2xl font-bold">{stats?.orders?.pending || 0}</p>
                </div>
                <Clock className="w-8 h-8 text-yellow-500/30" />
              </div>
            </CardContent>
          </Card>
          <Card className="rounded-sm cursor-pointer hover:ring-2 hover:ring-green-500/50 transition-all" data-testid="stat-points-redeemed" onClick={() => setActiveTab("orders")}>
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-muted-foreground uppercase">Points Redeemed</p>
                  <p className="text-2xl font-bold">{(stats?.orders?.total_points_redeemed || 0).toLocaleString()}</p>
                </div>
                <Gift className="w-8 h-8 text-primary/30" />
              </div>
            </CardContent>
          </Card>
          <Card className="rounded-sm cursor-pointer hover:ring-2 hover:ring-blue-500/50 transition-all" data-testid="stat-catalog-products" onClick={() => setActiveTab("catalog")}>
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-muted-foreground uppercase">Catalog Products</p>
                  <p className="text-2xl font-bold">{stats?.catalog?.total_products || 0}</p>
                </div>
                <Package className="w-8 h-8 text-primary/30" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Main Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
          <TabsList className="grid w-full grid-cols-5 rounded-none h-12">
            <TabsTrigger value="orders" className="rounded-none data-[state=active]:bg-primary data-[state=active]:text-primary-foreground" data-testid="tab-orders">
              <Truck className="w-4 h-4 mr-2" />Orders
            </TabsTrigger>
            <TabsTrigger value="catalog" className="rounded-none data-[state=active]:bg-primary data-[state=active]:text-primary-foreground" data-testid="tab-catalog">
              <Package className="w-4 h-4 mr-2" />Catalog
            </TabsTrigger>
            <TabsTrigger value="verified" className="rounded-none data-[state=active]:bg-primary data-[state=active]:text-primary-foreground" data-testid="tab-verified">
              <BadgeCheck className="w-4 h-4 mr-2" />Verified
            </TabsTrigger>
            <TabsTrigger value="logs" className="rounded-none data-[state=active]:bg-primary data-[state=active]:text-primary-foreground" data-testid="tab-logs">
              <FileText className="w-4 h-4 mr-2" />Callback Logs
            </TabsTrigger>
            <TabsTrigger value="api-logs" className="rounded-none data-[state=active]:bg-primary data-[state=active]:text-primary-foreground" data-testid="tab-api-logs">
              <AlertCircle className="w-4 h-4 mr-2" />API Logs
            </TabsTrigger>
          </TabsList>

          {/* Orders Tab */}
          <TabsContent value="orders">
            <Card className="rounded-sm">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>Orders & Fulfillment ({orderStats.filtered || orders.length})</CardTitle>
                    <CardDescription>Click any row to view full order details. Use the Update Status button to change fulfillment.</CardDescription>
                  </div>
                  <div className="flex gap-2">
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={() => handleExportOrders('csv')}
                      disabled={exportLoading}
                      className="rounded-none"
                    >
                      {exportLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileText className="w-4 h-4 mr-2" />}
                      Export CSV
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={() => handleExportOrders('json')}
                      disabled={exportLoading}
                      className="rounded-none"
                    >
                      Export JSON
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={handleBackfillUserTypes}
                      disabled={backfillLoading}
                      className="rounded-none"
                    >
                      {backfillLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Users className="w-4 h-4 mr-2" />}
                      Backfill User Types
                    </Button>
                    <Button
                      variant="default"
                      size="sm"
                      onClick={handleDownloadTajReport}
                      disabled={tajReportLoading}
                      className="rounded-none"
                      data-testid="taj-report-btn"
                    >
                      {tajReportLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileText className="w-4 h-4 mr-2" />}
                      Taj Gift Report (CSV)
                    </Button>
                  </div>
                </div>
                {/* Search & Filters */}
                <div className="flex flex-wrap items-center gap-3 mt-4 p-3 bg-muted rounded-sm">
                  <div className="flex items-center gap-2 flex-1 min-w-[220px]">
                    <Search className="w-4 h-4 text-muted-foreground shrink-0" />
                    <Input
                      placeholder="Search by Order ID"
                      value={orderIdSearch}
                      onChange={(e) => setOrderIdSearch(e.target.value)}
                      className="rounded-none"
                      data-testid="orders-search-order-id"
                    />
                  </div>
                  <div className="flex items-center gap-2 flex-1 min-w-[220px]">
                    <Search className="w-4 h-4 text-muted-foreground shrink-0" />
                    <Input
                      placeholder="Search by Mobile Number"
                      value={mobileSearch}
                      onChange={(e) => setMobileSearch(e.target.value)}
                      className="rounded-none"
                      data-testid="orders-search-mobile"
                    />
                  </div>
                  {(orderIdSearch || mobileSearch) && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => { setOrderIdSearch(""); setMobileSearch(""); }}
                      className="rounded-none"
                      data-testid="orders-clear-search"
                    >
                      Clear Search
                    </Button>
                  )}
                </div>
                {/* Date Range Filter */}
                <div className="flex flex-wrap items-center gap-4 mt-4 p-3 bg-muted rounded-sm">
                  <div className="flex items-center gap-2">
                    <Label className="text-sm whitespace-nowrap">From:</Label>
                    <Input 
                      type="date" 
                      value={orderStartDate} 
                      onChange={(e) => setOrderStartDate(e.target.value)}
                      className="w-40 rounded-none"
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <Label className="text-sm whitespace-nowrap">To:</Label>
                    <Input 
                      type="date" 
                      value={orderEndDate} 
                      onChange={(e) => setOrderEndDate(e.target.value)}
                      className="w-40 rounded-none"
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <Label className="text-sm whitespace-nowrap">User Type:</Label>
                    <Select value={userTypeFilter} onValueChange={setUserTypeFilter}>
                      <SelectTrigger className="w-36 rounded-none">
                        <SelectValue placeholder="All Types" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Types</SelectItem>
                        <SelectItem value="dealer">Dealer</SelectItem>
                        <SelectItem value="sub_dealer">Sub Dealer</SelectItem>
                        <SelectItem value="influencer">Influencer</SelectItem>
                        <SelectItem value="atr">ATR</SelectItem>
                        {userTypes.filter(ut => !["dealer", "sub_dealer", "influencer", "atr"].includes(ut.toLowerCase())).map(ut => (
                          <SelectItem key={ut} value={ut}>{ut}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={() => { setOrderStartDate(""); setOrderEndDate(""); setUserTypeFilter("all"); }}
                    className="rounded-none"
                  >
                    Clear Filters
                  </Button>
                  <div className="flex-1" />
                  <div className="flex gap-1 flex-wrap">
                    {["all", "pending", "processing", "shipped", "delivered", "cancelled"].map(status => (
                      <Button key={status} variant={orderFilter === status ? "default" : "outline"} size="sm" onClick={() => setOrderFilter(status)} className="capitalize rounded-none text-xs">
                        {status} {status !== "all" && `(${orderStats[status] || 0})`}
                      </Button>
                    ))}
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Order ID</TableHead>
                      <TableHead>Date</TableHead>
                      <TableHead>Item</TableHead>
                      <TableHead>Points</TableHead>
                      <TableHead>Customer</TableHead>
                      <TableHead>User Type</TableHead>
                      <TableHead>Address</TableHead>
                      <TableHead>Caller</TableHead>
                      <TableHead>Taj Confirm</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Tracking</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {orders.map(order => (
                      <TableRow
                        key={order.order_id}
                        className="cursor-pointer hover:bg-muted/50"
                        data-testid={`order-row-${order.order_id}`}
                        onClick={() => openOrderDetails(order)}
                      >
                        <TableCell className="font-mono text-xs">{order.order_id}</TableCell>
                        <TableCell className="text-xs whitespace-nowrap">{order.created_at?.slice(0, 10) || "-"}</TableCell>
                        <TableCell>
                          <div className="max-w-[200px]">
                            <p className="font-medium truncate" data-testid={`order-item-name-${order.order_id}`}>{order.item_name || order.product_name || "Unknown Item"}</p>
                            <p className="text-xs text-muted-foreground">{order.item_category && <span className="mr-1">{order.item_category} &middot;</span>}{order.item_id}</p>
                          </div>
                        </TableCell>
                        <TableCell className="font-bold">{order.points_redeemed?.toLocaleString()}</TableCell>
                        <TableCell>
                          <p className="text-sm font-medium">{order.user_name || "-"}</p>
                          <p className="text-xs text-muted-foreground">{order.mobile}</p>
                          <p className="text-xs text-muted-foreground">ID: {order.taj_user_id || "-"}</p>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className="capitalize text-xs">
                            {order.user_type || "-"}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {order.delivery_address ? (
                            <div className="text-xs max-w-[150px]">
                              <p className="truncate">{order.delivery_address.line1}</p>
                              <p>{order.delivery_address.city}, {order.delivery_address.pincode}</p>
                            </div>
                          ) : "-"}
                        </TableCell>
                        <TableCell className="text-xs">
                          {order.caller_name || <span className="text-muted-foreground">—</span>}
                        </TableCell>
                        <TableCell>
                          {order.taj_confirmation === "yes" && <Badge className="bg-green-100 text-green-800">Yes</Badge>}
                          {order.taj_confirmation === "no" && <Badge className="bg-red-100 text-red-800">No</Badge>}
                          {(!order.taj_confirmation || order.taj_confirmation === "pending") && (
                            <Badge variant="outline" className="text-xs">Pending</Badge>
                          )}
                        </TableCell>
                        <TableCell>{getStatusBadge(order.fulfillment_status)}</TableCell>
                        <TableCell>
                          {order.tracking_number ? (
                            <div className="text-xs">
                              <p className="font-mono">{order.tracking_number}</p>
                              <p className="text-muted-foreground">{order.courier_partner}</p>
                            </div>
                          ) : "-"}
                        </TableCell>
                        <TableCell onClick={(e) => e.stopPropagation()}>
                          <Button variant="outline" size="sm" onClick={() => openOrderStatusDialog(order)} className="rounded-none" data-testid={`update-status-btn-${order.order_id}`}>
                            Update Status
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                    {orders.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={12} className="text-center text-muted-foreground py-8">No orders found</TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>

                {/* Pagination */}
                <div className="flex justify-between items-center mt-4">
                  <p className="text-sm text-muted-foreground">
                    Showing {orders.length} of {orderStats.filtered || 0} orders
                    {orderPages > 1 && ` — Page ${orderPage} of ${orderPages}`}
                  </p>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={orderPage === 1}
                      onClick={() => setOrderPage(p => Math.max(1, p - 1))}
                      className="rounded-none"
                      data-testid="orders-prev-page"
                    >
                      Previous
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={orderPage >= orderPages}
                      onClick={() => setOrderPage(p => p + 1)}
                      className="rounded-none"
                      data-testid="orders-next-page"
                    >
                      Next
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Catalog Tab */}
          <TabsContent value="catalog">
            <Card className="rounded-sm">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>Product Catalog ({catalogTotal})</CardTitle>
                    <CardDescription>
                      Manage rewards catalog — Physical items and E-Vouchers.{" "}
                      <span className="text-primary">Redemption rule: customer must have ≥ 2 × MRP points to redeem.</span>
                    </CardDescription>
                  </div>
                  <div className="flex gap-2 flex-wrap">
                    <input
                      type="file"
                      accept=".csv"
                      onChange={handleCSVUpload}
                      className="hidden"
                      id="csv-upload"
                    />
                    <Button 
                      variant="outline" 
                      onClick={() => document.getElementById('csv-upload').click()}
                      disabled={uploadLoading}
                      className="rounded-none"
                    >
                      {uploadLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileText className="w-4 h-4 mr-2" />}
                      Upload CSV
                    </Button>
                    <Button 
                      variant="outline" 
                      onClick={handleClearCatalog}
                      className="rounded-none text-destructive hover:text-destructive"
                    >
                      <Trash2 className="w-4 h-4 mr-2" />
                      Clear All
                    </Button>
                    <Button onClick={() => { resetProductForm(); setProductDialogOpen(true); }} className="rounded-none">
                      <Plus className="w-4 h-4 mr-2" />Add Product
                    </Button>
                  </div>
                </div>
                {/* CSV Format Help */}
                <div className="mt-2 p-3 bg-muted rounded-sm text-xs">
                  <p className="font-medium mb-1">CSV Format:</p>
                  <p className="text-muted-foreground">PRODUCT CODE, PRODUCT NAME, BRAND NAME, MRP, Final Transfer Price, Tax %, HSN Code, Image URL</p>
                </div>
              </CardHeader>
              <CardContent>
                {/* Search & Filters */}
                <div className="flex gap-4 mb-4">
                  <div className="flex-1 relative">
                    <Search className="w-4 h-4 absolute left-3 top-3 text-muted-foreground" />
                    <Input placeholder="Search products..." value={catalogSearch} onChange={(e) => { setCatalogSearch(e.target.value); setCatalogPage(1); }} className="pl-10 rounded-none" />
                  </div>
                  <Select value={catalogCategory} onValueChange={(v) => { setCatalogCategory(v); setCatalogPage(1); }}>
                    <SelectTrigger className="w-[200px] rounded-none"><SelectValue placeholder="Category" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Categories</SelectItem>
                      {categories.map(cat => <SelectItem key={cat} value={cat}>{cat}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Product</TableHead>
                      <TableHead>Category</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Points</TableHead>
                      <TableHead>MRP</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {catalogProducts.map(product => (
                      <TableRow key={product.id}>
                        <TableCell>
                          <div className="flex items-center gap-3">
                            {product.image_url && <img src={product.image_url} alt={product.name} className="w-10 h-10 object-cover rounded" />}
                            <div className="max-w-[200px]">
                              <p className="font-medium truncate">{product.name}</p>
                              <p className="text-xs text-muted-foreground">{product.id}</p>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>{product.category}</TableCell>
                        <TableCell><Badge variant="outline">{product.item_type}</Badge></TableCell>
                        <TableCell className="font-bold">{product.points_required?.toLocaleString()}</TableCell>
                        <TableCell>₹{product.mrp?.toLocaleString() || "-"}</TableCell>
                        <TableCell>{product.in_stock ? <Badge className="bg-green-100 text-green-800">In Stock</Badge> : <Badge className="bg-red-100 text-red-800">Out of Stock</Badge>}</TableCell>
                        <TableCell>
                          <div className="flex gap-2">
                            <Button variant="ghost" size="icon" onClick={() => openEditProduct(product)}><Pencil className="w-4 h-4" /></Button>
                            <Button variant="ghost" size="icon" onClick={() => handleDeleteProduct(product.id)}><Trash2 className="w-4 h-4 text-destructive" /></Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                
                {/* Pagination */}
                <div className="flex justify-between items-center mt-4">
                  <p className="text-sm text-muted-foreground">Showing {catalogProducts.length} of {catalogTotal} products</p>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" disabled={catalogPage === 1} onClick={() => setCatalogPage(p => p - 1)} className="rounded-none">Previous</Button>
                    <Button variant="outline" size="sm" disabled={catalogProducts.length < 20} onClick={() => setCatalogPage(p => p + 1)} className="rounded-none">Next</Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Verified Users Tab */}
          <TabsContent value="verified">
            <Card className="rounded-sm">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>Verified Users ({verifiedUsers.length})</CardTitle>
                    <CardDescription>Users who completed KYC verification</CardDescription>
                  </div>
                  <div className="flex items-center gap-2">
                    <Label className="text-sm whitespace-nowrap">User Type:</Label>
                    <Select value={verifiedUserTypeFilter} onValueChange={setVerifiedUserTypeFilter}>
                      <SelectTrigger className="w-36 rounded-none">
                        <SelectValue placeholder="All Types" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Types</SelectItem>
                        <SelectItem value="dealer">Dealer</SelectItem>
                        <SelectItem value="sub_dealer">Sub Dealer</SelectItem>
                        <SelectItem value="influencer">Influencer</SelectItem>
                        <SelectItem value="atr">ATR</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Mobile</TableHead>
                      <TableHead>User Type</TableHead>
                      <TableHead>Verification ID</TableHead>
                      <TableHead>Verified At</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {verifiedUsers
                      .filter(user => verifiedUserTypeFilter === "all" || user.user_type?.toLowerCase() === verifiedUserTypeFilter.toLowerCase())
                      .map(user => (
                      <TableRow key={user.mobile}>
                        <TableCell className="font-medium">{user.name}</TableCell>
                        <TableCell>{user.mobile}</TableCell>
                        <TableCell>
                          <Badge variant="outline" className="capitalize text-xs">
                            {user.user_type || "-"}
                          </Badge>
                        </TableCell>
                        <TableCell className="font-mono text-xs">{user.verification_ref_id}</TableCell>
                        <TableCell className="text-xs">{new Date(user.verified_at).toLocaleString()}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Logs Tab */}
          <TabsContent value="logs">
            <Card className="rounded-sm">
              <CardHeader>
                <CardTitle>Callback Logs ({callbacks.length})</CardTitle>
                <CardDescription>API callback history</CardDescription>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Type</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Details</TableHead>
                      <TableHead>Created</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {callbacks.slice(0, 20).map((log, i) => (
                      <TableRow key={i}>
                        <TableCell><Badge variant="outline">{log.type}</Badge></TableCell>
                        <TableCell><Badge variant={log.status === "success" ? "default" : "destructive"}>{log.status}</Badge></TableCell>
                        <TableCell className="max-w-[300px] truncate text-xs">{JSON.stringify(log.payload || log.response)?.slice(0, 100)}...</TableCell>
                        <TableCell className="text-xs">{new Date(log.created_at).toLocaleString()}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          {/* API Logs Tab */}
          <TabsContent value="api-logs">
            <Card className="rounded-sm">
              <CardHeader>
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div>
                    <CardTitle>API Logs ({apiLogsFilteredTotal.toLocaleString()})</CardTitle>
                    <CardDescription>
                      All /api requests & responses.{" "}
                      <span className="text-muted-foreground">
                        Total {apiLogsStats?.total?.toLocaleString?.() || 0} · 4xx {apiLogsStats?.errors_4xx || 0} · 5xx {apiLogsStats?.errors_5xx || 0}
                      </span>
                    </CardDescription>
                  </div>
                  <div className="flex gap-2 flex-wrap">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={loadApiLogs}
                      disabled={apiLogsLoading}
                      className="rounded-none"
                      data-testid="reload-api-logs-btn"
                    >
                      {apiLogsLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
                      Refresh
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleClearApiLogs(7)}
                      disabled={clearLogsLoading}
                      className="rounded-none"
                    >
                      Clear &gt; 7 days
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleClearApiLogs(null)}
                      disabled={clearLogsLoading}
                      className="rounded-none text-destructive hover:text-destructive"
                    >
                      <Trash2 className="w-4 h-4 mr-2" />
                      Clear All
                    </Button>
                  </div>
                </div>

                {/* Filters */}
                <div className="flex flex-wrap items-center gap-2 mt-4 p-3 bg-muted rounded-sm">
                  <div className="flex items-center gap-2">
                    <Label className="text-sm">Method:</Label>
                    <Select
                      value={apiLogsFilters.method}
                      onValueChange={(v) => setApiLogsFilters({ ...apiLogsFilters, method: v })}
                    >
                      <SelectTrigger className="w-28 rounded-none" data-testid="api-logs-method-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All</SelectItem>
                        <SelectItem value="GET">GET</SelectItem>
                        <SelectItem value="POST">POST</SelectItem>
                        <SelectItem value="PUT">PUT</SelectItem>
                        <SelectItem value="DELETE">DELETE</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex items-center gap-2">
                    <Label className="text-sm">Status:</Label>
                    <Select
                      value={apiLogsFilters.status_class}
                      onValueChange={(v) => setApiLogsFilters({ ...apiLogsFilters, status_class: v })}
                    >
                      <SelectTrigger className="w-32 rounded-none" data-testid="api-logs-status-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All</SelectItem>
                        <SelectItem value="2xx">2xx Success</SelectItem>
                        <SelectItem value="4xx">4xx Client Err</SelectItem>
                        <SelectItem value="5xx">5xx Server Err</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex items-center gap-2 flex-1 min-w-[220px]">
                    <Label className="text-sm whitespace-nowrap">Path contains:</Label>
                    <Input
                      className="rounded-none"
                      placeholder="e.g. rewards, orders, taj"
                      value={apiLogsFilters.path_contains}
                      onChange={(e) => setApiLogsFilters({ ...apiLogsFilters, path_contains: e.target.value })}
                      data-testid="api-logs-path-input"
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <Label className="text-sm">From:</Label>
                    <Input
                      type="date"
                      className="rounded-none w-40"
                      value={apiLogsFilters.start_date}
                      onChange={(e) => setApiLogsFilters({ ...apiLogsFilters, start_date: e.target.value })}
                    />
                    <Label className="text-sm">To:</Label>
                    <Input
                      type="date"
                      className="rounded-none w-40"
                      value={apiLogsFilters.end_date}
                      onChange={(e) => setApiLogsFilters({ ...apiLogsFilters, end_date: e.target.value })}
                    />
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setApiLogsFilters({ method: "all", status_class: "all", path_contains: "", start_date: "", end_date: "" })}
                    className="rounded-none"
                  >
                    Clear Filters
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Time</TableHead>
                      <TableHead>Method</TableHead>
                      <TableHead>Path</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Duration</TableHead>
                      <TableHead>Client</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {apiLogs.map((log, i) => {
                      const s = log.status_code || 0;
                      const statusClass = s >= 500 ? "bg-red-100 text-red-800" : s >= 400 ? "bg-yellow-100 text-yellow-800" : s >= 300 ? "bg-blue-100 text-blue-800" : "bg-green-100 text-green-800";
                      return (
                        <TableRow
                          key={i}
                          className="cursor-pointer hover:bg-muted/50"
                          onClick={() => openLogDetails(log)}
                          data-testid={`api-log-row-${i}`}
                        >
                          <TableCell className="text-xs whitespace-nowrap">
                            {log.timestamp ? new Date(log.timestamp).toLocaleString() : "-"}
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline" className="font-mono text-xs">{log.method}</Badge>
                          </TableCell>
                          <TableCell className="font-mono text-xs max-w-[380px] truncate">
                            {log.path}{log.query ? `?${log.query}` : ""}
                          </TableCell>
                          <TableCell>
                            <Badge className={`${statusClass} text-xs`}>{log.status_code}</Badge>
                          </TableCell>
                          <TableCell className="text-xs">{log.duration_ms}ms</TableCell>
                          <TableCell className="text-xs max-w-[160px] truncate">{log.client_ip || "-"}</TableCell>
                        </TableRow>
                      );
                    })}
                    {apiLogs.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={6} className="text-center text-muted-foreground py-8">
                          {apiLogsLoading ? "Loading…" : "No API logs found"}
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>

                {/* Pagination */}
                <div className="flex justify-between items-center mt-4">
                  <p className="text-sm text-muted-foreground">
                    Showing {apiLogs.length} of {apiLogsFilteredTotal.toLocaleString()}
                    {apiLogsPages > 1 && ` — Page ${apiLogsPage} of ${apiLogsPages}`}
                  </p>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" disabled={apiLogsPage === 1} onClick={() => setApiLogsPage(p => Math.max(1, p - 1))} className="rounded-none">Previous</Button>
                    <Button variant="outline" size="sm" disabled={apiLogsPage >= apiLogsPages} onClick={() => setApiLogsPage(p => p + 1)} className="rounded-none">Next</Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Product Dialog */}
      <Dialog open={productDialogOpen} onOpenChange={setProductDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{editingProduct ? "Edit Product" : "Add New Product"}</DialogTitle>
            <DialogDescription>Fill in the product details below</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4 py-4">
            <div className="space-y-2">
              <Label>Name *</Label>
              <Input value={productForm.name} onChange={e => setProductForm({...productForm, name: e.target.value})} />
            </div>
            <div className="space-y-2">
              <Label>Brand</Label>
              <Input value={productForm.brand} onChange={e => setProductForm({...productForm, brand: e.target.value})} />
            </div>
            <div className="col-span-2 space-y-2">
              <Label>Description</Label>
              <Textarea value={productForm.description} onChange={e => setProductForm({...productForm, description: e.target.value})} />
            </div>
            <div className="space-y-2">
              <Label>Category</Label>
              <Select value={productForm.category} onValueChange={v => setProductForm({...productForm, category: v})}>
                <SelectTrigger><SelectValue placeholder="Select category" /></SelectTrigger>
                <SelectContent>
                  {categories.map(cat => <SelectItem key={cat} value={cat}>{cat}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Type</Label>
              <Select value={productForm.item_type} onValueChange={v => setProductForm({...productForm, item_type: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="physical">Physical</SelectItem>
                  <SelectItem value="voucher">Voucher</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Points Required *</Label>
              <Input type="number" value={productForm.points_required} onChange={e => setProductForm({...productForm, points_required: e.target.value})} />
            </div>
            <div className="space-y-2">
              <Label>MRP (₹)</Label>
              <Input type="number" value={productForm.mrp} onChange={e => setProductForm({...productForm, mrp: e.target.value})} />
              <p className="text-xs text-muted-foreground">Used for redemption eligibility (customer needs ≥ 2 × MRP points).</p>
            </div>
            <div className="col-span-2 space-y-2">
              <Label>Image URL</Label>
              <Input value={productForm.image_url} onChange={e => setProductForm({...productForm, image_url: e.target.value})} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setProductDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSaveProduct} disabled={productLoading}>
              {productLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {editingProduct ? "Update" : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Order Status Dialog */}
      <Dialog open={orderDialogOpen} onOpenChange={setOrderDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Update Order Status</DialogTitle>
            <DialogDescription>Order: {selectedOrder?.order_id}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Status *</Label>
              <Select value={orderStatusForm.status} onValueChange={v => setOrderStatusForm({...orderStatusForm, status: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="processing">Processing</SelectItem>
                  <SelectItem value="shipped">Shipped</SelectItem>
                  <SelectItem value="delivered">Delivered</SelectItem>
                  <SelectItem value="cancelled">Cancelled</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Tracking Number</Label>
              <Input value={orderStatusForm.tracking_number} onChange={e => setOrderStatusForm({...orderStatusForm, tracking_number: e.target.value})} placeholder="e.g., DTDC123456789" />
            </div>
            <div className="space-y-2">
              <Label>Courier Partner</Label>
              <Input value={orderStatusForm.courier_partner} onChange={e => setOrderStatusForm({...orderStatusForm, courier_partner: e.target.value})} placeholder="e.g., DTDC, BlueDart" />
            </div>
            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea value={orderStatusForm.notes} onChange={e => setOrderStatusForm({...orderStatusForm, notes: e.target.value})} placeholder="Add any notes about this status update" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOrderDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleUpdateOrderStatus}>Update Status</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Order Details Drill-Down Dialog */}
      <Dialog open={orderDetailsOpen} onOpenChange={setOrderDetailsOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="order-details-dialog">
          <DialogHeader>
            <DialogTitle>Order Details</DialogTitle>
            <DialogDescription className="font-mono">{detailsOrder?.order_id}</DialogDescription>
          </DialogHeader>
          {detailsOrder && (
            <div className="space-y-5 py-2 text-sm">
              {/* Status banner */}
              <div className="flex items-center justify-between border-b pb-3">
                <div>
                  <p className="text-xs text-muted-foreground uppercase tracking-wide">Fulfillment Status</p>
                  <div className="mt-1">{getStatusBadge(detailsOrder.fulfillment_status)}</div>
                </div>
                <div className="text-right">
                  <p className="text-xs text-muted-foreground uppercase tracking-wide">Points Redeemed</p>
                  <p className="text-2xl font-bold">{detailsOrder.points_redeemed?.toLocaleString() || 0}</p>
                </div>
              </div>

              {/* Item */}
              <section>
                <h3 className="font-semibold text-base mb-2">Item</h3>
                <div className="grid grid-cols-2 gap-3">
                  <Detail label="Item Name" value={detailsOrder.item_name || detailsOrder.product_name} />
                  <Detail label="Item ID" value={detailsOrder.item_id} mono />
                  <Detail label="Category" value={detailsOrder.item_category} />
                  <Detail label="Item Type" value={detailsOrder.item_type} />
                  <Detail label="MRP" value={detailsOrder.mrp ? `₹${Number(detailsOrder.mrp).toLocaleString()}` : "-"} />
                  <Detail label="Provider" value={detailsOrder.provider} />
                </div>
              </section>

              {/* Customer */}
              <section>
                <h3 className="font-semibold text-base mb-2">Customer</h3>
                <div className="grid grid-cols-2 gap-3">
                  <Detail label="Name" value={detailsOrder.user_name} />
                  <Detail label="Mobile" value={detailsOrder.mobile} />
                  <Detail label="Taj User ID (UID)" value={detailsOrder.taj_user_id} mono />
                  <Detail label="User Type" value={detailsOrder.user_type} />
                  <Detail label="Email" value={detailsOrder.email} />
                </div>
              </section>

              {/* Caller Info (editable) */}
              <section className="border rounded-sm p-4 bg-muted/30">
                <h3 className="font-semibold text-base mb-3 flex items-center justify-between">
                  <span>Caller Info & Taj Confirmation</span>
                  {detailsOrder.taj_confirmation === "yes" && (
                    <Badge className="bg-green-100 text-green-800">Confirmed — Ready to Dispatch</Badge>
                  )}
                  {detailsOrder.taj_confirmation === "no" && (
                    <Badge className="bg-red-100 text-red-800">Rejected</Badge>
                  )}
                  {(!detailsOrder.taj_confirmation || detailsOrder.taj_confirmation === "pending") && (
                    <Badge variant="outline">Awaiting Taj Confirmation</Badge>
                  )}
                </h3>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs">Caller Name *</Label>
                    <Input
                      data-testid="caller-name-input"
                      value={callerForm.caller_name}
                      onChange={(e) => setCallerForm({ ...callerForm, caller_name: e.target.value })}
                      placeholder="Enter caller's name"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Status (Taj Confirmation)</Label>
                    <Select
                      value={callerForm.taj_confirmation}
                      onValueChange={(v) => setCallerForm({ ...callerForm, taj_confirmation: v })}
                    >
                      <SelectTrigger data-testid="taj-confirmation-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="pending">Pending</SelectItem>
                        <SelectItem value="yes">Yes — Dispatch Approved</SelectItem>
                        <SelectItem value="no">No — Do Not Dispatch</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </section>

              {/* Delivery Address (editable) */}
              <section>
                <h3 className="font-semibold text-base mb-2">Delivery Address (editable)</h3>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1 col-span-2">
                    <Label className="text-xs">Line 1</Label>
                    <Input
                      data-testid="addr-line1-input"
                      value={callerForm.delivery_address.line1}
                      onChange={(e) =>
                        setCallerForm({
                          ...callerForm,
                          delivery_address: { ...callerForm.delivery_address, line1: e.target.value },
                        })
                      }
                    />
                  </div>
                  <div className="space-y-1 col-span-2">
                    <Label className="text-xs">Line 2</Label>
                    <Input
                      data-testid="addr-line2-input"
                      value={callerForm.delivery_address.line2}
                      onChange={(e) =>
                        setCallerForm({
                          ...callerForm,
                          delivery_address: { ...callerForm.delivery_address, line2: e.target.value },
                        })
                      }
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">City</Label>
                    <Input
                      data-testid="addr-city-input"
                      value={callerForm.delivery_address.city}
                      onChange={(e) =>
                        setCallerForm({
                          ...callerForm,
                          delivery_address: { ...callerForm.delivery_address, city: e.target.value },
                        })
                      }
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">District</Label>
                    <Input
                      data-testid="addr-district-input"
                      value={callerForm.delivery_address.district}
                      onChange={(e) =>
                        setCallerForm({
                          ...callerForm,
                          delivery_address: { ...callerForm.delivery_address, district: e.target.value },
                        })
                      }
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">State</Label>
                    <Input
                      data-testid="addr-state-input"
                      value={callerForm.delivery_address.state}
                      onChange={(e) =>
                        setCallerForm({
                          ...callerForm,
                          delivery_address: { ...callerForm.delivery_address, state: e.target.value },
                        })
                      }
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Pincode</Label>
                    <Input
                      data-testid="addr-pincode-input"
                      value={callerForm.delivery_address.pincode}
                      onChange={(e) =>
                        setCallerForm({
                          ...callerForm,
                          delivery_address: { ...callerForm.delivery_address, pincode: e.target.value },
                        })
                      }
                    />
                  </div>
                </div>
                <div className="flex justify-end mt-3">
                  <Button
                    onClick={handleSaveCallerInfo}
                    disabled={callerSaving}
                    data-testid="save-caller-info-btn"
                    className="rounded-none"
                  >
                    {callerSaving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                    Save Caller Info & Address
                  </Button>
                </div>
              </section>

              {/* Fulfillment */}
              <section>
                <h3 className="font-semibold text-base mb-2">Fulfillment</h3>
                <div className="grid grid-cols-2 gap-3">
                  <Detail label="Tracking Number" value={detailsOrder.tracking_number} mono />
                  <Detail label="Courier Partner" value={detailsOrder.courier_partner} />
                  <Detail label="Created At" value={detailsOrder.created_at ? new Date(detailsOrder.created_at).toLocaleString() : "-"} />
                  <Detail label="Last Updated" value={detailsOrder.updated_at ? new Date(detailsOrder.updated_at).toLocaleString() : "-"} />
                </div>
                {detailsOrder.notes && (
                  <div className="mt-3">
                    <Detail label="Notes" value={detailsOrder.notes} />
                  </div>
                )}
              </section>

              {/* Status history */}
              {Array.isArray(detailsOrder.status_history) && detailsOrder.status_history.length > 0 && (
                <section>
                  <h3 className="font-semibold text-base mb-2">Status History</h3>
                  <div className="space-y-2">
                    {detailsOrder.status_history.map((h, i) => (
                      <div key={i} className="flex items-start justify-between border rounded-sm p-2">
                        <div>
                          <p className="font-medium capitalize">{h.status}</p>
                          {h.notes && <p className="text-xs text-muted-foreground">{h.notes}</p>}
                        </div>
                        <p className="text-xs text-muted-foreground whitespace-nowrap">
                          {h.timestamp ? new Date(h.timestamp).toLocaleString() : ""}
                        </p>
                      </div>
                    ))}
                  </div>
                </section>
              )}
            </div>
          )}
          <DialogFooter>
            <Button
              variant="destructive"
              onClick={handleDeleteOrder}
              disabled={deleteOrderLoading}
              className="rounded-none mr-auto"
              data-testid="delete-order-btn"
            >
              {deleteOrderLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Trash2 className="w-4 h-4 mr-2" />}
              Delete Order
            </Button>
            <Button
              variant="outline"
              onClick={() => {
                setOrderDetailsOpen(false);
                openOrderStatusDialog(detailsOrder);
              }}
              data-testid="details-update-status-btn"
            >
              Update Status
            </Button>
            <Button onClick={() => setOrderDetailsOpen(false)} data-testid="details-close-btn">Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* API Log Details Dialog */}
      <Dialog open={logDetailsOpen} onOpenChange={setLogDetailsOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="api-log-details-dialog">
          <DialogHeader>
            <DialogTitle>API Request Details</DialogTitle>
            <DialogDescription className="font-mono text-xs">
              {selectedLog?.method} {selectedLog?.path}{selectedLog?.query ? `?${selectedLog.query}` : ""}
            </DialogDescription>
          </DialogHeader>
          {selectedLog && (
            <div className="space-y-4 py-2 text-sm">
              <div className="grid grid-cols-4 gap-3 border-b pb-3">
                <Detail label="Timestamp" value={selectedLog.timestamp ? new Date(selectedLog.timestamp).toLocaleString() : "-"} />
                <Detail label="Status Code" value={String(selectedLog.status_code || "")} />
                <Detail label="Duration" value={`${selectedLog.duration_ms}ms`} />
                <Detail label="Client IP" value={selectedLog.client_ip} mono />
              </div>
              <Detail label="User Agent" value={selectedLog.user_agent} />

              <section>
                <h3 className="font-semibold text-sm mb-2">Request Headers</h3>
                <pre className="text-xs bg-muted p-3 rounded-sm overflow-x-auto max-h-40 whitespace-pre-wrap break-all">
{JSON.stringify(selectedLog.request_headers || {}, null, 2)}
                </pre>
              </section>

              <section>
                <h3 className="font-semibold text-sm mb-2">Request Body</h3>
                <pre className="text-xs bg-muted p-3 rounded-sm overflow-x-auto max-h-72 whitespace-pre-wrap break-all">
{selectedLog.request_body || "(empty)"}
                </pre>
              </section>

              <section>
                <h3 className="font-semibold text-sm mb-2">Response Body</h3>
                <pre className="text-xs bg-muted p-3 rounded-sm overflow-x-auto max-h-72 whitespace-pre-wrap break-all">
{selectedLog.response_body || "(empty)"}
                </pre>
              </section>
            </div>
          )}
          <DialogFooter>
            <Button onClick={() => setLogDetailsOpen(false)} data-testid="close-api-log-details">Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Footer */}
      <footer className="border-t py-4 px-4 mt-8">
        <div className="max-w-7xl mx-auto flex items-center justify-center gap-3">
          <span className="text-sm text-muted-foreground">Powered by</span>
          <img src={require("@/assets/tagnpay-logo.png")} alt="Tag'N'Pay" className="h-6 w-auto" />
        </div>
      </footer>
    </div>
  );
}
